import { YAHOO_TARGET_FIELDS, yahooTargetError } from './yahooTargeting';
import { YAHOO_EDITABLE } from './yahooReview';

export const YAHOO_BULK_PLACEHOLDER = {
  title: 'What would you like to change?',
  body: 'I can change flight dates, budgets, bids, names, goal amounts and targeting before launch. Targeting edits replace comma-separated lists; geography, segment and category edits require verified Yahoo IDs. Targeting applies to all flights of a line. Line names, bids and goals apply to every flight of the same line. I’ll show the changes for your confirmation.',
  sectionLabel: 'Try asking',
  prompts: ['Set the selected flights to January 1–31, 2027.', 'Set the selected currency flight budgets to 5000.', 'Set the bid price to 2 on all flights of this line.'],
};

// The last check before agent edits enter the normal manual-edit save path.
// Yahoo only: existing platforms retain their own staging semantics.
export function yahooBulkStagingError(staging, { reviewRows = [], selectedRowIds = [], alreadyLaunched } = {}) {
  if (alreadyLaunched) return 'Yahoo bulk edits are available before launch only.';
  if (staging?.rowOps?.length || Object.keys(staging?.listOps || {}).length) return 'Yahoo row and targeting-list operations are not supported.';
  const selected = new Set([...selectedRowIds].map(String));
  const byId = new Map(reviewRows.map((r) => [String(r.rowId), r]));
  const edits = staging?.edits || {};
  for (const [id, fields] of Object.entries(edits)) {
    const row = byId.get(id);
    if (!row || (selected.size && !selected.has(id))) return 'Yahoo proposal contains a row outside the current plan selection; request a new preview.';
    for (const [field, value] of Object.entries(fields)) {
      if (!YAHOO_EDITABLE.has(field)) return `${field} is read-only in the Yahoo bulk editor.`;
      if (Object.hasOwn(YAHOO_TARGET_FIELDS, field)) {
        const error = yahooTargetError(field, value, row);
        if (error) return error;
      } else if (field === 'name') {
        if (typeof value !== 'string' || !value.trim()) return 'Yahoo line names cannot be empty.';
      } else if (field === 'start' || field === 'end') {
        const ms = Date.parse(`${value}T00:00:00Z`);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value)) || !Number.isFinite(ms) || new Date(ms).toISOString().slice(0, 10) !== value) return 'Yahoo flight date is invalid.';
      } else {
        if (value == null || value === '' || !Number.isFinite(Number(value)) || Number(value) < 0) return `Yahoo ${field} must be a non-negative number.`;
        if (field === 'imps' && !Number.isInteger(Number(value))) return 'Yahoo impressions must be a whole number.';
        if (['budget', 'imps', 'dailyBudget'].includes(field)) {
          if (!['CURRENCY', 'IMPRESSION'].includes(row.budgetType)
            || (field === 'imps') !== (row.budgetType === 'IMPRESSION')) return 'The Yahoo budget edit does not match the flight’s budget type.';
        }
      }
      if (['name', 'bidFloor', 'goalAmount', ...Object.keys(YAHOO_TARGET_FIELDS)].includes(field)) {
        if (!row.yahooLineId) return 'Refresh the Yahoo plan before changing line-level fields.';
        const siblings = reviewRows.filter((r) => r.yahooLineId === row.yahooLineId);
        if (siblings.some((r) => !Object.prototype.hasOwnProperty.call(edits[r.rowId] || {}, field)
          || String(edits[r.rowId][field]) !== String(value))) return `Yahoo ${field} must have one value across all flights of the same line. Select all of those flights and request a new preview.`;
      }
    }
  }
  return null;
}
